import { Box, Hidden, NoSsr } from '@mui/material';
import SVG from 'react-inlinesvg';
import { makeStyles } from 'tss-react/mui';
import React, {
  memo,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
} from 'react-beautiful-dnd';
import Typography from '@ui/Typography';
import { paletteV2 } from 'theme/palette';
import CircularLoader from '../LoadingIndicators/CircularLoader';
import {
  ProjectAccessAction,
  ProjectAccessSubject,
} from '../../../server/abilities/ProjectAccessEnums';
import { ClipObject, instanceOfCompletedClip } from '../../context/clips';
import { useEventsContext } from '../../context/events/EventsContext';
import {
  Can,
  useProjectAccessAbility,
} from '../../context/projectAccess/ProjectAccessContext';
import useStudioClipCompleteStyles from '../StudioClip/States/Complete/completeStyles';
import StudioClipWrapper from '../StudioClip/StudioClipWrapper';
import StudioClipList from '../StudioClipList';
import { StudioClipListProps } from '../StudioClipList/StudioClipList';
import useStudioClips from './hooks/useStudioClips/index';
import StudioClipUserListBatchActions from './StudioClipUserListBatchActions';
import StudioClipWithUserActions, {
  StudioClipWithUserActionsProps,
} from './StudioClipWithUserActions';
import useClipEntityChangedSubscription from './hooks/useStudioClips/useClipEntityChangedSubscription';
import { usePersistentClipStateAndActions } from './hooks/useStudioClips/common';
import { getCoreEditorRef } from '../../context/editorV2/CoreEditorContext';
import storage from '../../util/storage';
import { AVATAR_MODAL } from '../Projects/useOnProjectCreationSuccess';
import useIsFirstRender from '../../lib/useIsFirstRender';
import { Project } from '../../../server/graphql/__generated.types';
import { selectEditMode, useAppSelector } from '../../store/hooks';
import { EditMode } from '../../store/EditorSlice';
import { ActorVariant } from './hooks/useStudioClips/utils/constants';
import Sticky from '../Sticky';
import useEvaluateStudioProjectVersionFeatures from '../Studio/useEvaluateStudioProjectVersionFeatures';
import useIsLegacyProjectViewOnly from '../Studio/legacy/useIsLegacyProjectViewOnly';

const DROPPABLE_ID = 'StudioClipUserList-droppable';

const useStyles = makeStyles()(theme => ({
  stickyActionBarActive: {
    zIndex: 1000,
    backgroundColor: theme.palette.common.black,
    boxShadow: theme.shadows[1],
  },
  stickActionBarPadding: {
    padding: `${theme.spacing(1)} 0`,
  },
  searchContainer: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(5.5),
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    flexDirection: 'column',
    paddingTop: theme.spacing(25),
  },
  noClipsText: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

const StudioClipUserList = ({
  project,
  onReplaceEditorValue,
  setActiveActor,
  onPromptDisplay,
  visible = true,
  setDownloadError,
}: StudioClipUserListProps) => {
  const { classes, cx } = useStyles();
  const completeClasses = useStudioClipCompleteStyles();
  const [currentProjectId, setCurrentProjectId] = useState(project.id);
  const projectAccessAbility = useProjectAccessAbility();
  const isFirstRender = useIsFirstRender();
  useEffect(() => {
    if (project.id !== currentProjectId) {
      setCurrentProjectId(project.id);
    }
  }, [project.id, currentProjectId]);
  const [
    {
      clips,
      variables,
      loading,
      error,
      selectedClipIds,
      previouslySelectedClip,
      hasNextPage,
    },
    {
      fetchMore,
      deleteClip,
      renameClip,
      setPreviouslySelectedClip,
      setSelectedClipIds,
      updateClipOrdering,
    },
  ] = useStudioClips(currentProjectId);
  const { search } = usePersistentClipStateAndActions();
  const projectVersionFeatures =
    useEvaluateStudioProjectVersionFeatures(project);
  const avatarModal = storage.getItem(AVATAR_MODAL);

  const editMode = useAppSelector(selectEditMode);
  const isCuesMode = editMode === EditMode.CUES;

  const canEditAProject = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return projectAccessAbility.can(
      ProjectAccessAction.Edit,
      ProjectAccessSubject.Project
    );
  }, [projectAccessAbility]);
  const isLegacyViewOnly = useIsLegacyProjectViewOnly(project.studioVersion);
  const showNotFound = useMemo(() => {
    return !loading && clips.length === 0 && variables?.clipsInputs.search;
  }, [clips.length, loading, variables?.clipsInputs.search]);
  /**
   * @todo ideally we should not show this when the `editorValue` state of editor
   * is populated but there is no mechanism AFAIK to wait for loaded value in session
   * storage - it initially will render as '' before loading the value
   */
  useEffect(() => {
    const shouldNotRender = !isFirstRender || isCuesMode;
    if (shouldNotRender) {
      return;
    }
    // if avatar storage reference - immediately invoke prompt without loading
    if (avatarModal === 'true') {
      onPromptDisplay();

      // reset storage so modal only renders on first visit to project
      storage.setItem(AVATAR_MODAL, 'false');
    }
  }, [
    clips.length,
    loading,
    onPromptDisplay,
    canEditAProject,
    avatarModal,
    isFirstRender,
    isCuesMode,
  ]);

  const [eventTracking] = useEventsContext();
  useClipEntityChangedSubscription(currentProjectId);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }
      if (result.source.index === result.destination.index) {
        return;
      }

      const clip = clips[result.source.index];
      const newClip = clips[result.destination.index];

      // NOTE: if either clip is incomplete we cancel the sort since the
      // incomplete clip is not yet defined on the server.
      if (!instanceOfCompletedClip(clip) || !instanceOfCompletedClip(newClip)) {
        return;
      }

      const newIndex = result.destination.index;

      let precedingClipIndex: number;

      if (newIndex === 0) {
        precedingClipIndex = -1;
      } else if (result.source.index < result.destination.index) {
        precedingClipIndex = result.destination.index;
      } else {
        precedingClipIndex = result.destination.index - 1;
      }

      const precedingClip =
        precedingClipIndex === -1 ? undefined : clips[precedingClipIndex];

      updateClipOrdering({
        clip,
        newIndex,
        precedingClip:
          precedingClip && instanceOfCompletedClip(precedingClip)
            ? precedingClip
            : undefined,
      });
      eventTracking.trackClipReorder();
    },
    [updateClipOrdering, clips, eventTracking]
  );

  const handleSelectedChange = useCallback(
    (clipId: string, selected: boolean, opts?: { isMultiSelect?: boolean }) => {
      const { isMultiSelect } = { isMultiSelect: false, ...opts };

      if (selected) {
        setSelectedClipIds(selectedClipIds.add(clipId));
        if (isMultiSelect && previouslySelectedClip) {
          const clipIndex = clips.findIndex(clip => clip.id === clipId);
          const previousClipIndex = clips.findIndex(
            clip => clip.id === previouslySelectedClip
          );
          const toSelect = clips
            .slice(
              Math.min(clipIndex, previousClipIndex),
              Math.max(clipIndex, previousClipIndex) + 1
            )
            .map(clip => clip.id);
          setSelectedClipIds(selectedClipIds.union(toSelect));
        }
        setPreviouslySelectedClip(clipId);
      } else {
        setSelectedClipIds(selectedClipIds.remove(clipId));
      }
    },
    [
      selectedClipIds,
      setSelectedClipIds,
      clips,
      previouslySelectedClip,
      setPreviouslySelectedClip,
    ]
  );

  const editorRef = getCoreEditorRef();
  const handleReplaceEditorValue = useCallback(
    (newValue: string) => {
      onReplaceEditorValue(newValue);

      if (editorRef) {
        editorRef.setContent(newValue);
      }
    },
    [onReplaceEditorValue, editorRef]
  );

  const clipRenderer = useMemo<StudioClipListProps['clipRenderer']>(
    () =>
      // eslint-disable-next-line react/no-unstable-nested-components
      ({ clip, index }) => {
        // Only include the Draggable component for completed clips
        const isComplete = instanceOfCompletedClip(clip);
        const studioClipProps = {
          clip,
          onDelete: deleteClip,
          onRename: renameClip,
          onReplaceEditorValue: handleReplaceEditorValue,
          selected: selectedClipIds.contains(clip.id),
          onSelectedChange: handleSelectedChange,
          setActiveActor,
          studioClipCompleteClasses: completeClasses,
          projectId: project.id,
          setDownloadError,
        };
        return isComplete ? (
          <Can
            I={ProjectAccessAction.Move}
            a={ProjectAccessSubject.Clip}
            passThrough
          >
            {(allowed: boolean) => (
              <Draggable
                key={clip.id}
                draggableId={clip.id}
                index={index}
                isDragDisabled={!allowed || !!search || isLegacyViewOnly}
              >
                {(
                  provided: DraggableProvided,
                  snapshot: DraggableStateSnapshot
                ) => (
                  <StudioClipWrapper draggableProvided={provided}>
                    <StudioClipWithUserActions
                      {...studioClipProps}
                      draggableProvided={allowed ? provided : undefined}
                      isDragging={snapshot.isDragging}
                      project={project}
                    />
                  </StudioClipWrapper>
                )}
              </Draggable>
            )}
          </Can>
        ) : (
          <StudioClipWrapper>
            <StudioClipWithUserActions {...studioClipProps} project={project} />
          </StudioClipWrapper>
        );
      },
    [
      deleteClip,
      renameClip,
      handleReplaceEditorValue,
      selectedClipIds,
      handleSelectedChange,
      setActiveActor,
      completeClasses,
      search,
      project,
      setDownloadError,
      isLegacyViewOnly,
    ]
  );

  const handleRenderDroppableClone = useCallback(
    (
      provided: DraggableProvided,
      snapshot: DraggableStateSnapshot,
      rubric: DraggableRubric
    ) => {
      const clip = clips[rubric.source.index];
      if (!clip) return <div>{`Clip not found!`}</div>;
      return (
        <StudioClipWrapper draggableProvided={provided}>
          <StudioClipWithUserActions
            clip={clip}
            onDelete={deleteClip}
            onRename={renameClip}
            onReplaceEditorValue={onReplaceEditorValue}
            selected={selectedClipIds.contains(clip.id)}
            onSelectedChange={handleSelectedChange}
            setActiveActor={setActiveActor}
            studioClipCompleteClasses={completeClasses}
            draggableProvided={provided}
            isDragging={snapshot.isDragging}
            projectId={project.id}
            project={project}
          />
        </StudioClipWrapper>
      );
    },
    [
      completeClasses,
      deleteClip,
      clips,
      handleSelectedChange,
      onReplaceEditorValue,
      renameClip,
      selectedClipIds,
      setActiveActor,
      project.id,
    ]
  );

  const batchActionsContainerRef = useRef<HTMLDivElement>(null);

  if (!loading && clips.length === 0 && !canEditAProject)
    return (
      <div className={classes.centered}>
        <SVG
          src={`/static/images/studio/zeroClips.svg`}
          color={paletteV2.grey[75]}
        />
        <div className={classes.noClipsText}>
          <Typography variant="h3" bold color="gray_75">
            No clips yet
          </Typography>
          <Typography variant="pMd" color="gray_75">
            This project is empty.
          </Typography>
        </div>
      </div>
    );

  return !visible ? null : (
    <>
      {error && (
        <Box position="absolute" top={-24}>
          <Typography role="alert" color="error" variant="pSm">
            {`Error getting clips, please try again later.`}
          </Typography>
        </Box>
      )}

      <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
        <Hidden smDown>
          <div style={{ marginBottom: 2 }} ref={batchActionsContainerRef}>
            <Sticky
              className={cx(
                classes.stickyActionBarActive,
                classes.stickActionBarPadding
              )}
              observedRef={batchActionsContainerRef}
            >
              <Box>
                <StudioClipUserListBatchActions
                  project={project}
                  searchDisabled={projectVersionFeatures.searchDisabled}
                  setDownloadError={setDownloadError}
                />
              </Box>
            </Sticky>
          </div>
        </Hidden>
      </Can>

      <Box position="relative" paddingBottom={10}>
        <NoSsr>
          {loading && clips?.length === 0 && (
            <Box className={classes.searchContainer}>
              <CircularLoader role="alert" aria-label="Loading clips" />
            </Box>
          )}
          {showNotFound && (
            <Typography
              variant="pMd"
              className={classes.searchContainer}
              role="alert"
            >
              No results found for{' '}
              <Typography variant="pMd" bold>
                &ldquo;{search}&rdquo;
              </Typography>
            </Typography>
          )}

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable
              droppableId={DROPPABLE_ID}
              mode="virtual"
              renderClone={handleRenderDroppableClone}
            >
              {(droppableProvided: DroppableProvided) => (
                <div
                  // eslint-disable-next-line @typescript-eslint/unbound-method
                  ref={droppableProvided.innerRef}
                >
                  <StudioClipList
                    clips={clips}
                    autoloadFn={fetchMore}
                    autoloadDisabled={loading || !!error || !hasNextPage}
                    clipRenderer={clipRenderer}
                    droppableProvided={droppableProvided}
                  />
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </NoSsr>
      </Box>
    </>
  );
};

export interface StudioClipUserListProps
  extends Pick<StudioClipWithUserActionsProps, 'onReplaceEditorValue'> {
  setActiveActor: (
    actor: Pick<ClipObject['actor'], 'id' | 'variant'>,
    variant?: ActorVariant,
    ttsVersion?: string
  ) => void;
  project: Project;
  onReplaceEditorValue: (value: string) => void;
  onPromptDisplay: () => void;
  /**
   * prop exists to avoid re-rendering component to listen for
   * initial render lifecycle
   */
  visible?: boolean;
  setDownloadError?: (downloadError: string) => void;
}

export default memo(StudioClipUserList);
